import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const DOMAIN = 'https://joinkrieger.com';

// Pages that should NOT be indexed (private/auth-gated)
const NOINDEX_PATHS = ['/dashboard', '/admin', '/payment'];

export default function useSEO({ title, description, keywords, canonical } = {}) {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;

    // Set title
    if (title) document.title = title;

    // Helper to set/create meta tags
    const setMeta = (name, content) => {
      if (!content) return;
      const selector = name.startsWith('og:') || name.startsWith('twitter:')
        ? `meta[property="${name}"]`
        : `meta[name="${name}"]`;
      let el = document.querySelector(selector);
      if (el) {
        el.setAttribute('content', content);
      } else {
        el = document.createElement('meta');
        el.setAttribute(name.startsWith('og:') || name.startsWith('twitter:') ? 'property' : 'name', name);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };

    if (description) {
      setMeta('description', description);
      setMeta('og:description', description);
      setMeta('twitter:description', description);
    }
    if (title) {
      setMeta('og:title', title);
      setMeta('twitter:title', title);
    }
    if (keywords) setMeta('keywords', keywords);

    // Canonical URL — always set, using explicit value or auto-derived from route
    const isNoIndex = NOINDEX_PATHS.some(p => path.startsWith(p));
    const canonicalUrl = canonical || (isNoIndex ? null : `${DOMAIN}${path === '/' ? '' : path}`);

    // Update or create the canonical link element
    let link = document.querySelector('link[rel="canonical"]');
    if (canonicalUrl) {
      if (link) {
        link.setAttribute('href', canonicalUrl);
      } else {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', canonicalUrl);
        document.head.appendChild(link);
      }
      setMeta('og:url', canonicalUrl);
    } else if (isNoIndex && link) {
      // Remove canonical for noindex pages
      link.remove();
    }

    // Set robots meta for private pages
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (isNoIndex) {
      if (robotsMeta) robotsMeta.setAttribute('content', 'noindex, nofollow');
    } else {
      if (robotsMeta) robotsMeta.setAttribute('content', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    }

  }, [title, description, keywords, canonical, location.pathname]);
}
